import React, { useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';

export interface BillablePrintContext {
  sourceType: 'edgemail' | 'edgedocs' | 'browser';
  sourceId?: string;
  title: string;
  matterId?: string | null;
  matterDisplayId?: string | null;
  clientDisplayId?: string | null;
  pageCount: number;
  defaultUnitCost?: number;
  includePdfAttachments?: boolean;
  description?: string;
}

export interface PrintBillingApprovalResult {
  approved: boolean;
  billable: boolean;
  expenseId?: string;
}

const DEFAULT_PRINT_UNIT_COST = 0.10;
const SOFT_COST_REVIEW_RATE = 0.10;
const SOFT_COST_REVIEW_TOTAL = 25;
const PRINT_UTBMS_EXPENSE_CODE = 'E101';

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('webelieve_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function createPrintExpense(input: {
  matterId: string;
  title: string;
  sourceType: BillablePrintContext['sourceType'];
  sourceId?: string;
  pageCount: number;
  unitCost: number;
  totalAmount: number;
  billable: boolean;
  requiresApproval: boolean;
  includePdfAttachments?: boolean;
}): Promise<string | undefined> {
  const res = await fetch('/api/v1/billing/expenses', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      matter_id: input.matterId,
      expense_date: todayIsoDate(),
      description: `Print job: ${input.title}`,
      expense_category: 'copying',
      utbms_expense_code: PRINT_UTBMS_EXPENSE_CODE,
      vendor: 'EdgeLex Print',
      reference_number: [input.sourceType, input.sourceId].filter(Boolean).join(':') || undefined,
      is_non_billable: !input.billable,
      show_on_invoice: input.billable,
      requires_approval: input.requiresApproval,
      recoverability: input.billable ? (input.requiresApproval ? 'pending' : 'billable') : 'absorbed',
      quantity: input.pageCount,
      unit_type: 'pages',
      unit_cost: input.unitCost,
      total_amount: input.totalAmount,
      is_taxable: false,
      receipt_required: false,
      has_receipt: false,
      notes: [
        `Automated print billing capture from ${input.sourceType}.`,
        `Source ID: ${input.sourceId || 'n/a'}.`,
        'Soft cost recovery item. Client charge should be consistent with the engagement agreement, applicable OCGs, and actual cost plus reasonable overhead.',
        input.requiresApproval ? 'Flagged for billing review because the rate or total exceeds the default soft-cost review threshold.' : '',
        input.includePdfAttachments ? 'Includes PDF attachments.' : '',
      ].filter(Boolean).join('\n'),
    }),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(json?.description || json?.error || json?.message || `Failed to record print billing expense (${res.status}).`);
  }
  return json?.data?.expense_id;
}

function PrintBillingModal({
  context,
  resolve,
}: {
  context: BillablePrintContext;
  resolve: (result: PrintBillingApprovalResult) => void;
}) {
  const [pageCount, setPageCount] = useState(Math.max(1, Math.round(context.pageCount || 1)));
  const [unitCost, setUnitCost] = useState(context.defaultUnitCost ?? DEFAULT_PRINT_UNIT_COST);
  const [billable, setBillable] = useState(Boolean(context.matterId));
  const [recordBilling, setRecordBilling] = useState(Boolean(context.matterId));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const total = useMemo(() => Number((Math.max(0, pageCount) * Math.max(0, unitCost)).toFixed(2)), [pageCount, unitCost]);
  const canRecordBilling = Boolean(context.matterId);
  const requiresSoftCostReview = billable && recordBilling && (unitCost > SOFT_COST_REVIEW_RATE || total > SOFT_COST_REVIEW_TOTAL);

  const close = (result: PrintBillingApprovalResult) => {
    resolve(result);
  };

  const approve = async () => {
    if (submitting) return;
    setError(null);
    if (recordBilling && !context.matterId) {
      setError('Link this print job to a matter before recording billing.');
      return;
    }

    try {
      setSubmitting(true);
      let expenseId: string | undefined;
      if (recordBilling && context.matterId) {
        expenseId = await createPrintExpense({
          matterId: context.matterId,
          title: context.title,
          sourceType: context.sourceType,
          sourceId: context.sourceId,
          pageCount,
          unitCost,
          totalAmount: total,
          billable,
          requiresApproval: requiresSoftCostReview,
          includePdfAttachments: context.includePdfAttachments,
        });
      }
      close({ approved: true, billable: recordBilling && billable, expenseId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to record print billing.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/55 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-lg border border-gray-700 bg-[#191b22] text-white shadow-2xl">
        <div className="border-b border-gray-700 bg-[#0f3c9f] px-5 py-4">
          <h2 className="text-lg font-semibold">Print Billing</h2>
          <p className="text-sm text-blue-100">Record this print job before sending it to the printer.</p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="rounded-md border border-gray-700 bg-black/20 p-3">
            <div className="text-sm font-medium">{context.title}</div>
            <div className="mt-1 text-xs text-gray-300">
              {context.sourceType === 'edgemail' ? 'EdgeMail' : context.sourceType === 'edgedocs' ? 'EdgeDocs' : 'Print'} job
              {context.includePdfAttachments ? ' with PDF attachments' : ''}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="block">
              <span className="mb-1 block text-gray-300">Client ID</span>
              <div className="rounded border border-gray-700 bg-[#23252d] px-3 py-2">{context.clientDisplayId || 'Not linked'}</div>
            </label>
            <label className="block">
              <span className="mb-1 block text-gray-300">Matter ID</span>
              <div className="rounded border border-gray-700 bg-[#23252d] px-3 py-2">{context.matterDisplayId || 'Not linked'}</div>
            </label>
            <label className="block">
              <span className="mb-1 block text-gray-300">Pages</span>
              <input
                type="number"
                min={1}
                value={pageCount}
                onChange={(event) => setPageCount(Math.max(1, Number(event.target.value) || 1))}
                className="w-full rounded border border-gray-600 bg-[#23252d] px-3 py-2 text-white"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-gray-300">Cost per page</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={unitCost}
                onChange={(event) => setUnitCost(Math.max(0, Number(event.target.value) || 0))}
                className="w-full rounded border border-gray-600 bg-[#23252d] px-3 py-2 text-white"
              />
            </label>
          </div>

          <div className="flex items-center justify-between rounded-md border border-emerald-700 bg-emerald-950/30 px-3 py-2">
            <div>
              <div className="text-sm font-medium">Billing total</div>
              <div className="text-xs text-emerald-200">UTBMS {PRINT_UTBMS_EXPENSE_CODE} · Copying · Soft cost · Unbilled expense</div>
            </div>
            <div className="text-xl font-semibold">${total.toFixed(2)}</div>
          </div>

          {recordBilling && billable && (
            <div className={`rounded-md border px-3 py-2 text-xs ${
              requiresSoftCostReview
                ? 'border-amber-700 bg-amber-950/30 text-amber-100'
                : 'border-blue-700 bg-blue-950/30 text-blue-100'
            }`}>
              {requiresSoftCostReview
                ? 'This charge will be flagged for billing review. Copy/print charges should comply with client OCGs or engagement terms and should not exceed actual cost plus reasonable overhead.'
                : 'This will be recorded as a billable soft-cost recovery item. Confirm client OCGs or engagement terms permit print/copy charges.'}
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={recordBilling}
              disabled={!canRecordBilling}
              onChange={(event) => setRecordBilling(event.target.checked)}
            />
            Record this print job in Billing
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={billable}
              disabled={!recordBilling}
              onChange={(event) => setBillable(event.target.checked)}
            />
            Charge client on invoice
          </label>

          {!canRecordBilling && (
            <div className="rounded-md border border-amber-700 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
              No linked matter was found. You can continue printing, but no billing expense can be recorded from this job.
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-xs text-red-100">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-700 px-5 py-4">
          <button
            type="button"
            onClick={() => close({ approved: false, billable: false })}
            disabled={submitting}
            className="rounded border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={approve}
            disabled={submitting}
            className="rounded bg-[#0f3c9f] px-4 py-2 text-sm font-medium text-white hover:bg-[#164bbd] disabled:opacity-60"
          >
            {submitting ? 'Recording...' : recordBilling && canRecordBilling ? 'Record & Print' : 'Print'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function requestPrintBillingApproval(context: BillablePrintContext): Promise<PrintBillingApprovalResult> {
  if (typeof document === 'undefined') {
    return Promise.resolve({ approved: true, billable: false });
  }

  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  return new Promise((resolve) => {
    const settle = (result: PrintBillingApprovalResult) => {
      root.unmount();
      container.remove();
      resolve(result);
    };
    root.render(<PrintBillingModal context={context} resolve={settle} />);
  });
}
